// 行动层：把 Core 下发的 legal_actions 翻译成牌桌对象上的直接操作与拖拽手势。
//
// 注意事项：这里**不做任何合法性判断**——能做什么完全由服务端的 legal_actions 决定，
// 前端只负责把每条 action 附着到对应的骰子、卡牌、棋子或座席，并原样回发。空列表＝只能等待。

import { roleImage } from "./assets.tsx";
import { abilityName, cardName } from "./glossary.ts";
import { placeDropId, playerDropId } from "./drag.tsx";
import type { DropPayload } from "./drag.tsx";
import type { LegalAction, Pending, State } from "./types.ts";

export type ActionVisual = "dice" | "area" | "attack" | "ability" | "pass" | "reveal" | "action";

export function DieShape({
  kind,
  value,
  showKind = false,
}: {
  kind: "d4" | "d6";
  value: string | number;
  showKind?: boolean | undefined;
}) {
  return (
    <span className={`die-model die-model-${kind}`}>
      <svg viewBox="0 0 48 48" aria-hidden="true" focusable="false">
        {kind === "d4" ? (
          <path
            className="die-shell"
            d="M24 3 C25.7 3 27.2 3.9 28.1 5.5 L45.2 36.5 C47.1 40 44.6 44 40.7 44 H7.3 C3.4 44 0.9 40 2.8 36.5 L19.9 5.5 C20.8 3.9 22.3 3 24 3 Z"
          />
        ) : (
          <rect className="die-shell" x="3" y="3" width="42" height="42" rx="10" />
        )}
      </svg>
      {showKind && <small>{kind.toUpperCase()}</small>}
      <b>{value}</b>
    </span>
  );
}


export interface ObjectAction {
  label: string;
  action: LegalAction;
  kind: "primary" | "danger" | "ghost" | "normal";
  visual: ActionVisual;
}

export interface DragSources {
  /** 无法绑定到实体对象的少量回合操作：掷骰、跳过、全体攻击。 */
  table: ObjectAction[];
  /** 直接点玩家、场所、能力或自己的角色卡即可执行。 */
  players: Map<string, ObjectAction[]>;
  places: Map<number, ObjectAction[]>;
  abilities: Map<string, ObjectAction[]>;
  role: ObjectAction | null;
  /** 需要目标的能力从符卡拖到玩家身上。 */
  abilityDrags: Map<string, DropPayload>;
  /** 拖动自己的棋子＝相邻移动（每个合法相邻场所一个落点）。 */
  ownSeal: DropPayload | null;
  /** 拖动装备＝夺取 / 交付；点击只有一个合法结果时直接执行。 */
  equipment: Map<string, DropPayload>;
  /** 点击或拖动实体牌堆抽牌。 */
  decks: Map<string, DropPayload>;
  /** 询问里若选项值是玩家，可把中央卡牌拖到对应玩家。 */
  prompt: DropPayload | null;
}

const DECK_LABEL: Record<string, string> = { hakurei: "博麗", moriya: "守矢", kleinji: "命蓮寺" };
const PLACE_LABEL: Record<string, string> = {
  hakurei: "博丽神社",
  moriya: "守矢神社",
  kleinji: "命莲寺",
  human_village: "人类村落",
  eientei: "永远亭",
  magic_forest: "魔法之森",
};

export function areaLabel(action: LegalAction): string {
  const place = PLACE_LABEL[action.place ?? ""] ?? "场所";
  switch (action.place) {
    case "hakurei":
      return "抽博麗卡";
    case "moriya":
      return "抽守矢卡";
    case "kleinji":
      return "抽命蓮寺卡";
    case "human_village":
      return `自选卡组：${DECK_LABEL[action.deck ?? ""] ?? "卡組"}`;
    case "eientei":
      return action.mode === "heal" ? "永远亭：回复 1" : "永远亭：伤害 2";
    case "magic_forest":
      return "魔法之森：夺取装备";
    default:
      return place;
  }
}

function samePromptValue(left: unknown, right: unknown): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

/** 优先回发 Core 枚举的完整动作，避免丢失 attack_all 等新增协议字段。 */
export function promptLegalAction(
  legal: LegalAction[],
  pending: Pending,
  value: unknown,
): LegalAction {
  const type = value === undefined ? "pass" : "respond";
  const exact = legal.find(
    (action) =>
      action.type === type &&
      action.prompt_id === pending.prompt_id &&
      (type === "pass" || samePromptValue(action.value, value)),
  );
  if (exact) return exact;
  return value === undefined
    ? { type: "pass", prompt_id: pending.prompt_id }
    : { type: "respond", prompt_id: pending.prompt_id, value };
}

function addObjectAction<K>(
  map: Map<K, ObjectAction[]>,
  key: K,
  action: ObjectAction,
): void {
  map.set(key, [...(map.get(key) ?? []), action]);
}

/** 把 legal_actions 映射到桌面实体；只有无法空间化的动作留在回合印记。 */
export function buildDragSources(
  state: State,
  legal: LegalAction[],
  pending: Pending | null,
  me: string,
): DragSources {
  const nameOf = (id: string): string =>
    state.players.find((player) => player.id === id)?.name ?? "未知玩家";
  const label = (id: string): string => cardName(id);
  const table: ObjectAction[] = [];
  const players = new Map<string, ObjectAction[]>();
  const places = new Map<number, ObjectAction[]>();
  const abilities = new Map<string, ObjectAction[]>();
  const abilityDrags = new Map<string, DropPayload>();
  const equipment = new Map<string, DropPayload>();
  const decks = new Map<string, DropPayload>();
  const adjacent = new Map<string, Record<string, unknown>>();
  let role: ObjectAction | null = null;

  for (const action of legal) {
    switch (action.type) {
      case "move":
        if (action.mode === "adjacent" && action.slot !== undefined) {
          adjacent.set(placeDropId(action.slot), action);
        } else if (action.mode === "dice") {
          table.push({ label: "掷 D4 + D6 移动", action, kind: "primary", visual: "dice" });
        }
        break;
      case "area": {
        const base = areaLabel(action);
        if (action.place === "eientei" && action.target) {
          if (!players.get(action.target)?.some((object) => object.action.type === "area" && object.action.place === "eientei")) {
            addObjectAction(players, action.target, {
              label: `永远亭：选择 ${nameOf(action.target)}`,
              action,
              kind: "normal",
              visual: "area",
            });
          }
        } else if (action.target) {
          addObjectAction(players, action.target, {
            label: `${base} → ${nameOf(action.target)}`,
            action,
            kind: action.mode === "damage" ? "danger" : "normal",
            visual: action.mode === "damage" ? "attack" : "area",
          });
        } else if (action.card) {
          equipment.set(action.card, {
            label: `${base} → ${label(action.card)}`,
            image: roleImage(action.card),
            actions: new Map([[playerDropId(me), action]]),
          });
        } else {
          const deck =
            action.place === "human_village"
              ? action.deck
              : action.place === "hakurei" || action.place === "moriya" || action.place === "kleinji"
                ? action.place
                : undefined;
          if (deck) {
            decks.set(deck, {
              label: base,
              image: null,
              back: deck,
              actions: new Map([[playerDropId(me), action]]),
            });
          } else {
            const slot = state.board.places.find((place) => place.place === action.place)?.slot;
            if (slot !== undefined) {
              addObjectAction(places, slot, {
                label: base,
                action,
                kind: action.mode === "damage" ? "danger" : "normal",
                visual: "area",
              });
            }
          }
        }
        break;
      }
      case "attack": {
        const targets = action.targets ?? [];
        if (targets.length === 1) {
          const target = targets[0] ?? "";
          addObjectAction(players, target, {
            label: `攻击 ${nameOf(target)}`,
            action,
            kind: "danger",
            visual: "attack",
          });
        } else if (targets.length > 1) {
          const mapped: ObjectAction = {
            label: action.attack_all ? "Master Spark：全体攻击" : `攻击 ${targets.map(nameOf).join("、")}`,
            action,
            kind: "danger",
            visual: "attack",
          };
          if (action.attack_all) addObjectAction(abilities, "marisa_master_spark", mapped);
          else table.push(mapped);
        }
        break;
      }
      case "ability": {
        const target = action.params?.["target"];
        const ability = action.ability ?? "";
        const name = abilityName(ability);
        const mapped: ObjectAction = {
          label: name,
          action,
          kind: "normal",
          visual: "ability",
        };
        if (target && ability) {
          const payload = abilityDrags.get(ability) ?? {
            label: name,
            image: null,
            visual: "spell",
            actions: new Map<string, Record<string, unknown>>(),
          };
          payload.actions.set(playerDropId(target), action);
          abilityDrags.set(ability, payload);
        } else if (target) {
          addObjectAction(players, target, mapped);
        } else if (ability) {
          addObjectAction(abilities, ability, mapped);
        } else {
          table.push(mapped);
        }
        break;
      }
      case "respond":
        if (pending?.player === me && pending.kind === "roll_dice" && action.prompt_id === pending.prompt_id) {
          table.push({ label: pending.options[0]?.label ?? "掷 D4 + D6", action, kind: "primary", visual: "dice" });
        }
        break;
      case "pass":
        if (pending?.player === me && action.prompt_id === pending.prompt_id) break;
        table.push({
          label:
            state.turn.step === "attack"
              ? "不攻击 · 结束回合"
              : state.turn.step === "area"
                ? "跳过场所行动"
                : "不用此能力",
          action,
          kind: "ghost",
          visual: "pass",
        });
        break;
      case "reveal":
        role = { label: "公開身份", action, kind: "ghost", visual: "reveal" };
        break;
      default:
        if (action.type !== "respond") {
          table.push({ label: "可用操作", action, kind: "normal", visual: "action" });
        }
        break;
    }
  }

  const promptPayload: DropPayload | null =
    pending && pending.player === me
      ? (() => {
          const actions = new Map<string, Record<string, unknown>>();
          for (const option of pending.options) {
            if (typeof option.value !== "string" || !state.players.some((p) => p.id === option.value)) {
              continue;
            }
            const response = promptLegalAction(legal, pending, option.value);
            actions.set(playerDropId(option.value), response);
            addObjectAction(players, option.value, {
              label: option.label,
              action: response,
              kind: "normal",
              visual: pending.private_info?.card ? "area" : "action",
            });
          }
          if (actions.size === 0) return null;
          return {
            label: pending.private_info?.card
              ? `将${cardName(pending.private_info.card.id)}用于谁`
              : "选择目标",
            image: roleImage(pending.private_info?.card?.id ?? null),
            actions,
          };
        })()
      : null;
  if (pending?.player === me && pending.kind === "yes_no" && pending.context?.ability) {
    const use = pending.options.find((option) => option.value === true);
    const response = legal.find((action) =>
      action.type === "respond" && action.prompt_id === pending.prompt_id && action.value === true,
    );
    if (use && response) {
      addObjectAction(abilities, pending.context.ability, {
        label: use.label,
        action: response,
        kind: "normal",
        visual: "ability",
      });
    }
  }

  return {
    table,
    players,
    places,
    abilities,
    role,
    abilityDrags,
    ownSeal:
      adjacent.size > 0
        ? { label: "相邻移动", image: null, actions: adjacent }
        : null,
    equipment,
    decks,
    prompt: promptPayload,
  };
}

export function waitingText(
  state: State,
  legal: LegalAction[],
  waitingFor: string,
  me: string,
): string | null {
  if (legal.length > 0) return null;
  if (waitingFor && waitingFor !== me) {
    const name = state.players.find((player) => player.id === waitingFor)?.name ?? "未知玩家";
    return `等待 ${name} 处理询问…`;
  }
  const turnName = state.players.find((player) => player.id === state.turn.player)?.name ?? "";
  return `等待 ${turnName} 行动…`;
}

